import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { concatLatestFrom } from '@ngrx/operators';
import { AuthService } from '@telespot/web-core';
import { AffiliatedUsersStore } from '../../../stores/user/affiliated-user.store';
import { WorkspaceMembersEffects } from '../../../stores/user/workspace-members.effects';
import { WorkspaceMemberData } from '../../../stores/user/workspace-members.state';
import { WorkspaceDetailEffects } from '../../../stores/workspace-detail.effects';
import { Observable } from 'rxjs';
import { filter, map, skip, tap } from 'rxjs/operators';

type FacadeEmptyState = Record<string, never>;

export type AffiliatedUserWithMembership = {
  id: string;
  username: string;
  avatarUrl?: string;
  emailVerified: boolean;
  isMemberOfWorkspace: boolean | undefined;

  /**
   * Affiliation date is necessary for pagination
   */
  affiliationDate: Date;
};

const PAGE_SIZE = 20;

type WorkspaceMemberView = {
  id: string;
  username: string;
  avatarUrl: string | undefined;
  emailVerified: boolean;
  isMemberOfWorkspace: boolean;
};

@Injectable()
export class WorkspaceMembersFacade extends ComponentStore<FacadeEmptyState> {
  public readonly organizationUsers$: Observable<Array<AffiliatedUserWithMembership>>;
  public readonly workspaceMembers$: Observable<Array<WorkspaceMemberView>>;

  constructor(
    public readonly affiliatedUsersStore: AffiliatedUsersStore,
    private readonly workspaceMembersEffects: WorkspaceMembersEffects,
    private readonly detailEffects: WorkspaceDetailEffects,
    private readonly authService: AuthService
  ) {
    super({});
    this.organizationUsers$ = this.select(
      this.affiliatedUsersStore.users$.pipe(skip(1)),
      this.workspaceMembersEffects.memory$.pipe(skip(1)),
      (users, memory) =>
        users.map((user) => ({
          id: user.id,
          username: user.username,
          avatarUrl: user.avatarUrl,
          emailVerified: user.emailVerified,
          isMemberOfWorkspace: memory[user.id] ? memory[user.id] !== 'NO_MEMBERSHIP' : undefined,
          affiliationDate: user.affiliationDate,
        }))
    );

    this.workspaceMembers$ = this.select(this.workspaceMembersEffects.workspaceMembers$, (members) =>
      members.map((member) => ({
        id: member.id,
        username: member.username,
        avatarUrl: member.avatarUrl,
        emailVerified: member.emailVerified,
        isMemberOfWorkspace: member.membershipId !== undefined,
      }))
    );

    this.checkMemberships(this.organizationUsers$);

    // Re-fetch the workspace detail counters after each successful add/remove.
    // Resolved through the parent DI scope: WorkspaceDetailEffects is provided at the
    // ts-workspace-detail level and the members route renders inside its <router-outlet>.
    this.refreshDetailOnMembershipChange(this.workspaceMembersEffects.membershipChanged$);
  }

  private readonly refreshDetailOnMembershipChange = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(tap(() => this.detailEffects.refreshWorkspace()))
  );

  // ============ Selectors ============

  public get workspaceId$() {
    return this.workspaceMembersEffects.workspaceId$;
  }

  public get organizationUsersLoading$() {
    return this.affiliatedUsersStore.loading$;
  }

  public get hasMoreOrganizationUsers$() {
    return this.affiliatedUsersStore.hasMoreItems$;
  }

  public get organizationUsersSearchTerm$() {
    return this.affiliatedUsersStore.searchTerm$;
  }

  public get organizationUsersPreviousTokens$() {
    return this.affiliatedUsersStore.previousUserTokens$;
  }

  public get organizationUsersNextToken$() {
    return this.affiliatedUsersStore.nextUserToken$;
  }

  public get organizationUsersTotalCount$() {
    return this.affiliatedUsersStore.totalUserCount$;
  }

  public get organizationUsersPageSize$() {
    return this.affiliatedUsersStore.pageSize$;
  }

  public get organizationUsersCounting$() {
    return this.affiliatedUsersStore.counting$;
  }

  public get membersLoading$() {
    return this.workspaceMembersEffects.membersLoading$;
  }

  public get hasMoreMembers$() {
    return this.workspaceMembersEffects.hasMoreMembers$;
  }

  public get membersSearchTerm$() {
    return this.workspaceMembersEffects.membersSearchTerm$;
  }

  public get error$() {
    return this.workspaceMembersEffects.error$;
  }

  public get membersPreviousTokens$() {
    return this.workspaceMembersEffects.membersPreviousTokens$;
  }

  public get membersNextToken$() {
    return this.workspaceMembersEffects.membersNextToken$;
  }

  public get membersTotalCount$() {
    return this.workspaceMembersEffects.membersTotalCount$;
  }

  public get membersPageSize$() {
    return this.workspaceMembersEffects.membersPageSize$;
  }

  public get membersCounting$() {
    return this.workspaceMembersEffects.membersCounting$;
  }

  public get membershipOperationError$() {
    return this.workspaceMembersEffects.memberOperationError$;
  }

  // ============ Effects ============

  /**
   * Initialize the facade with workspace context.
   * Organization context (id, maxUsers) is read from AuthService.
   * Coordinates initialization between WorkspaceMembersEffects and AffiliatedUsersStore.
   */
  public readonly initialize = this.effect((context$: Observable<{ workspaceId: string }>) =>
    context$.pipe(
      tap((context: { workspaceId: string }) => {
        const organization = this.authService.currentOrganizationValue;
        const organizationId = organization?.id ?? '';
        const maxUsers = organization?.license?.maxUsers ?? -1;

        // Initialize WorkspaceMembersEffects with workspace context and the proper page size
        this.workspaceMembersEffects.changePageSize(PAGE_SIZE);
        this.workspaceMembersEffects.setWorkspaceId(context.workspaceId);

        // Initialize AffiliatedUsersStore with organization context from AuthService and the proper page size
        this.affiliatedUsersStore.setOrganization({ id: organizationId, maxUsers });
        this.affiliatedUsersStore.changePageSize(PAGE_SIZE);
      })
    )
  );

  private readonly checkMemberships = this.effect(
    (organizationUsers$: Observable<Array<AffiliatedUserWithMembership>>) =>
      organizationUsers$.pipe(
        map((users) => users.filter((u) => u.isMemberOfWorkspace === undefined).map((u) => u.id)),
        filter((missing: string[]) => missing.length > 0),
        tap((missing: string[]) => this.workspaceMembersEffects.checkMembershipStatus(missing))
      )
  );

  // ============ Delegated effects ============

  /** Set search term for workspace members (right list). Delegates to WorkspaceMembersEffects. */
  public readonly setMembersSearchTerm = this.effect((term$: Observable<string>) =>
    term$.pipe(tap((term: string) => this.workspaceMembersEffects.setSearchTerm(term)))
  );

  /** Set search term for organization users (left list). Delegates to AffiliatedUsersStore. */
  public readonly setOrganizationUsersSearchTerm = this.effect((term$: Observable<string>) =>
    term$.pipe(tap((term: string) => this.affiliatedUsersStore.setSearchTerm(term)))
  );

  /** Load more workspace members (pagination). Delegates to WorkspaceMembersEffects. */
  public readonly loadMoreMembers = this.effect((lastItem$: Observable<WorkspaceMemberData | undefined>) =>
    lastItem$.pipe(
      tap((lastItem: WorkspaceMemberData | undefined) => this.workspaceMembersEffects.loadMoreMembers(lastItem))
    )
  );

  /** Load more organization users (pagination). */
  public readonly loadMoreOrganizationUsers = this.effect(
    (action$: Observable<AffiliatedUserWithMembership | undefined>) =>
      action$.pipe(
        tap((lastItem: AffiliatedUserWithMembership | undefined) => {
          const userDataItem = lastItem
            ? {
                id: lastItem.id,
                username: lastItem.username,
                emailVerified: lastItem.emailVerified,
                avatarUrl: lastItem.avatarUrl,
                affiliationDate: lastItem.affiliationDate,
                affiliationId: undefined,
              }
            : undefined;
          this.affiliatedUsersStore.loadMoreUsers(userDataItem);
        })
      )
  );

  // ============ Member Management Effects ============

  /**
   * Add a user as a member of the workspace.
   * Delegates to WorkspaceMembersEffects to perform the operation.
   */
  public readonly addMember = this.effect((user$: Observable<AffiliatedUserWithMembership>) =>
    user$.pipe(
      tap((user: AffiliatedUserWithMembership) => {
        this.workspaceMembersEffects.addMemberToWorkspace(user.id);
      })
    )
  );

  /**
   * Remove a user from the workspace members.
   * Gets the membership ID from memory and delegates to WorkspaceMembersEffects.
   */
  public readonly removeMember = this.effect((user$: Observable<AffiliatedUserWithMembership>) =>
    user$.pipe(
      concatLatestFrom(() => this.workspaceMembersEffects.memory$),
      tap(([user, memory]: [AffiliatedUserWithMembership, Record<string, string>]) => {
        const membershipId = memory[user.id];
        if (membershipId && membershipId !== 'NO_MEMBERSHIP') {
          this.workspaceMembersEffects.removeMemberFromWorkspace({
            userId: user.id,
            membershipId,
          });
        }
      })
    )
  );
}
